import { Container, Group, TextInput, Button } from '@mantine/core';
import { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { ICurrency } from '../../../libraries/currencies';
import {
  IProductRequest,
  addProductDetails,
  updateProductDetails,
  deleteProduct,
  IProduct
} from '../../../libraries/products';
import { StatusType } from '../../../libraries/zps';
import { AppState } from '../../client-redux/reducers';
import CurrencyInput from '../../components/currency-input';
import ModalComponent from '../../components/modal';
import { useStatusOperDialog } from '../../context/status-oper-dialog';
import { Language } from '../../languages/languageHandler';
import './styles.scss';

interface AddEditProductProps {
  product?: IProduct;
  mode: 'new' | 'edit';
  opened: boolean;
  close: () => void;
  onSaved?: () => void;
}

const AddEditProduct = ({ product, mode, opened, close, onSaved }: AddEditProductProps) => {
  const lang = useSelector<AppState, Language>(state => state.lang.lang);
  const currency = useSelector<AppState, ICurrency[]>(state => state.currencies.currencies);
  const controller = useRef(new AbortController()).current;
  const { showStatusOperDialog, handleApiError } = useStatusOperDialog();

  const [formData, setFormData] = useState<IProductRequest>({
    productid: product?.productid ?? '',
    name: product?.name || '',
    price: product?.price || 0
  });

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (product) {
      setFormData({
        productid: product.productid ?? '',
        name: product.name,
        price: product.price
      });
    } else {
      setFormData({ productid: '', name: '', price: 0 });
    }
  }, [product, mode]);

  const handleChange = (field: keyof IProductRequest, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Save
  const handleSave = async () => {
    setLoading(true);

    try {
      const response =
        mode === 'new'
          ? await addProductDetails(formData, controller)
          : await updateProductDetails(formData, controller);

      if (response?.data.statusOper?.code === 0) {
        showStatusOperDialog({
          type: StatusType.INFO,
          message: mode === 'new' ? lang.PRODUCTS_ADD_SUCCESS : lang.PRODUCTS_EDIT_SUCCESS,
          statusOper: response.data.statusOper
        });

        onSaved?.();
      } else {
        showStatusOperDialog({
          type: StatusType.ERROR,
          message: lang.GLOBAL_ERROR,
          statusOper: response.data.statusOper
        });
      }
    } catch (err: any) {
      handleApiError(err, lang.GLOBAL_ERROR);
    } finally {
      setLoading(false);
    }
  };

  // Delete
  const handleDelete = async () => {
    if (!product) return;

    setLoading(true);

    try {
      const response = await deleteProduct(product.entid, product.productid);

      if (response?.data.statusOper?.code === 0) {
        showStatusOperDialog({
          type: StatusType.INFO,
          message: lang.PRODUCTS_DELETE_SUCCESS,
          statusOper: response.data.statusOper,
          timeout: 0 // ✅ no auto-close
        });

        onSaved?.();
      } else {
        showStatusOperDialog({
          type: StatusType.ERROR,
          message: lang.GLOBAL_ERROR,
          statusOper: response.data.statusOper
        });
      }
    } catch (err: any) {
      handleApiError(err, lang.GLOBAL_ERROR);
    } finally {
      setLoading(false);
    }
  };

  const title = mode === 'new' ? lang.PRODUCTS_ADD : `${lang.PRODUCTS_EDIT} - ${product?.productid}`;

  return (
    <ModalComponent
      show={opened}
      handleClick={close}
      title={title}
      content={
        <Container className="product-details-modal">
          <Group className="product-details">
            <TextInput
              label={lang.PRODUCTS_ID}
              value={formData.productid}
              onChange={e => handleChange('productid', e.currentTarget.value)}
              disabled={mode !== 'new' || loading}
            />
            <TextInput
              label={lang.PRODUCTS_NAME}
              value={formData.name}
              onChange={e => handleChange('name', e.currentTarget.value)}
              disabled={loading}
            />
            <CurrencyInput
              value={formData.price}
              label={lang.PRODUCTS_PRICE}
              currencySymbol={currency[0]?.currencyStr}
              disabled={loading}
              onChange={value => handleChange('price', value)}
            />
          </Group>

          <Group className="product-buttons">
            <Button radius="md" onClick={handleSave} disabled={loading}>
              {lang.GLOBAL_SAVE_BUTTON}
            </Button>

            {mode === 'edit' && (
              <Button radius="md" onClick={handleDelete} disabled={loading}>
                {lang.GLOBAL_DELETE}
              </Button>
            )}
          </Group>
        </Container>
      }
    />
  );
};

export default AddEditProduct;
